import React, { useState, useEffect } from 'react';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import '../styles/design-system.css';

export default function AIIntegrationManager() {
  const [userProfile, setUserProfile] = useState(null);
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState('free');
  const [aiFeatures, setAiFeatures] = useState({
    conversationStarters: [],
    travelRecommendations: [],
    safetyAlerts: [],
    personalityInsights: null
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        
        const subscriptionStatus = attributes['custom:subscriptionStatus'] || 'free';
        
        setUserProfile({
          id: currentUser.userId,
          name: attributes.name || 'User',
          email: attributes.email,
          personalityType: attributes['custom:personalityType'],
          personalityScore: attributes['custom:personalityScore']
        });
        
        setUserSubscriptionStatus(subscriptionStatus);
        
        // Load AI features based on user profile
        loadAIFeatures(attributes['custom:personalityType'], subscriptionStatus);
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, []);

  const loadAIFeatures = (personalityType, subscriptionStatus) => {
    setIsLoading(true);
    
    // Simulate AI feature loading
    setTimeout(() => {
      const features = generateAIFeatures(personalityType, subscriptionStatus);
      setAiFeatures(features);
      setIsLoading(false);
    }, 1000);
  };

  const generateAIFeatures = (personalityType, subscriptionStatus) => {
    const isPaidUser = subscriptionStatus === 'paid' || subscriptionStatus === 'premium';
    
    const conversationStarters = [
      "Hi! I noticed we both love [travel style]. Have you been to [destination]?",
      "Your profile mentions [interest]. I'd love to hear about your experiences!",
      "I see you're planning a trip to [location]. Any tips for a first-timer?",
      "We seem to have similar travel preferences. Would you be interested in meeting up?",
      "Your travel photos look amazing! What's your favorite destination so far?"
    ];

    const travelRecommendations = [
      {
        destination: "Barcelona, Spain",
        reason: "Perfect for cultural explorers like you",
        matchScore: 92,
        highlights: ["Sagrada Familia", "Gothic Quarter", "Tapas tours"]
      },
      {
        destination: "Tokyo, Japan", 
        reason: "Great for adventure seekers",
        matchScore: 88,
        highlights: ["Shibuya Crossing", "Tsukiji Market", "Mount Fuji"]
      },
      {
        destination: "Reykjavik, Iceland",
        reason: "Ideal for nature lovers",
        matchScore: 85,
        highlights: ["Northern Lights", "Blue Lagoon", "Golden Circle"]
      }
    ];

    const safetyAlerts = [
      {
        type: "location",
        message: "Always meet in public places for first encounters",
        priority: "high"
      },
      {
        type: "communication", 
        message: "Share your location with trusted contacts when meeting new people",
        priority: "medium"
      },
      {
        type: "verification",
        message: "Verify profiles through our community reviews",
        priority: "high"
      }
    ];

    const personalityInsights = personalityType ? {
      type: personalityType,
      strengths: getPersonalityStrengths(personalityType),
      travelStyle: getTravelStyle(personalityType),
      compatibilityTips: getCompatibilityTips(personalityType)
    } : null;

    return {
      conversationStarters: isPaidUser ? conversationStarters : conversationStarters.slice(0, 2),
      travelRecommendations: isPaidUser ? travelRecommendations : travelRecommendations.slice(0, 1),
      safetyAlerts,
      personalityInsights
    };
  };

  const getPersonalityStrengths = (personalityType) => {
    const strengths = {
      'Adventure Seeker': ['Spontaneous', 'Open to new experiences', 'Great at breaking the ice'],
      'Cultural Explorer': ['Respectful of local customs', 'Great conversation starter', 'Knowledgeable about destinations'],
      'Relaxation Enthusiast': ['Calm and patient', 'Good listener', 'Creates peaceful travel atmosphere'],
      'Social Butterfly': ['Outgoing and friendly', 'Great at organizing group activities', 'Natural connector'],
      'Independent Wanderer': ['Self-sufficient', 'Respects others\' space', 'Reliable travel companion'],
      'Luxury Traveler': ['Attention to detail', 'High standards', 'Great at planning premium experiences'],
      'Budget Conscious': ['Resourceful', 'Great at finding deals', 'Practical travel advice'],
      'Family Oriented': ['Responsible', 'Safety-conscious', 'Great with group dynamics']
    };
    return strengths[personalityType] || ['Adaptable', 'Friendly', 'Travel enthusiast'];
  };

  const getTravelStyle = (personalityType) => {
    const styles = {
      'Adventure Seeker': 'Spontaneous and thrill-seeking',
      'Cultural Explorer': 'Immersive and educational',
      'Relaxation Enthusiast': 'Peaceful and rejuvenating',
      'Social Butterfly': 'Group-oriented and interactive',
      'Independent Wanderer': 'Solo-focused and self-guided',
      'Luxury Traveler': 'Premium and comfortable',
      'Budget Conscious': 'Value-focused and practical',
      'Family Oriented': 'Safe and family-friendly'
    };
    return styles[personalityType] || 'Balanced and adaptable';
  };

  const getCompatibilityTips = (personalityType) => {
    const tips = {
      'Adventure Seeker': 'Look for companions who share your enthusiasm for spontaneity',
      'Cultural Explorer': 'Connect with travelers who appreciate local experiences',
      'Relaxation Enthusiast': 'Find companions who respect your need for peaceful travel',
      'Social Butterfly': 'Seek out group-oriented travelers and event organizers',
      'Independent Wanderer': 'Connect with self-sufficient travelers who respect space',
      'Luxury Traveler': 'Match with travelers who appreciate premium experiences',
      'Budget Conscious': 'Find companions who share your value-focused approach',
      'Family Oriented': 'Connect with safety-conscious and responsible travelers'
    };
    return tips[personalityType] || 'Focus on shared interests and travel goals';
  };

  const renderAIFeatures = () => {
    const isPaidUser = userSubscriptionStatus === 'paid' || userSubscriptionStatus === 'premium';

    return (
      <div className="ai-integration-manager">
        {/* AI Conversation Starters */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-lg">💬</span>
            </div>
            <div>
              <h3 className="text-xl font-semibold">AI Conversation Starters</h3>
              <p className="text-gray-600">Break the ice with personalized conversation prompts</p>
            </div>
          </div>

          <div className="space-y-3">
            {aiFeatures.conversationStarters.map((starter, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-800">{starter}</p>
                <button className="text-primary text-sm mt-2 hover:underline">
                  Use this message
                </button>
              </div>
            ))}
            
            {!isPaidUser && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Upgrade to Premium</strong> to access unlimited AI conversation starters and personalized recommendations!
                </p>
              </div>
            )}
          </div>
        </div>

        {/* AI Travel Recommendations */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
              <span className="text-white text-lg">🌍</span>
            </div>
            <div>
              <h3 className="text-xl font-semibold">AI Travel Recommendations</h3>
              <p className="text-gray-600">Personalized destinations based on your personality</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {aiFeatures.travelRecommendations.map((rec, index) => (
              <div key={index} className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">{rec.destination}</h4>
                <p className="text-sm text-green-700 mb-2">{rec.reason}</p>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">
                    {rec.matchScore}% Match
                  </span>
                </div>
                <div className="text-xs text-green-600">
                  <strong>Highlights:</strong> {rec.highlights.join(', ')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Safety Features */}
        <div className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center">
              <span className="text-white text-lg">🛡️</span>
            </div>
            <div>
              <h3 className="text-xl font-semibold">AI Safety Alerts</h3>
              <p className="text-gray-600">Real-time safety recommendations and tips</p>
            </div>
          </div>

          <div className="space-y-3">
            {aiFeatures.safetyAlerts.map((alert, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${
                alert.priority === 'high' 
                  ? 'bg-red-50 border-red-400' 
                  : 'bg-yellow-50 border-yellow-400'
              }`}>
                <div className="flex items-start gap-3">
                  <span className={`text-sm px-2 py-1 rounded ${
                    alert.priority === 'high' 
                      ? 'bg-red-200 text-red-800' 
                      : 'bg-yellow-200 text-yellow-800'
                  }`}>
                    {alert.priority.toUpperCase()}
                  </span>
                  <p className="text-sm text-gray-700">{alert.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Personality Insights */}
        {aiFeatures.personalityInsights && (
          <div className="card p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-lg">🧠</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold">AI Personality Insights</h3>
                <p className="text-gray-600">Understanding your travel personality for better matches</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3">Your Travel Strengths</h4>
                <ul className="space-y-2">
                  {aiFeatures.personalityInsights.strengths.map((strength, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      <span className="text-sm text-gray-700">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Travel Style</h4>
                <p className="text-sm text-gray-700 mb-4">
                  {aiFeatures.personalityInsights.travelStyle}
                </p>
                
                <h4 className="font-semibold mb-3">Compatibility Tips</h4>
                <p className="text-sm text-gray-700">
                  {aiFeatures.personalityInsights.compatibilityTips}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* AI Integration Status */}
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-white text-lg">🤖</span>
            </div>
            <div>
              <h3 className="text-xl font-semibold">AI Integration Status</h3>
              <p className="text-gray-600">Your AI features across web and mobile platforms</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">✅ Active Features</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Personality-based matching</li>
                <li>• Safety recommendations</li>
                <li>• Basic conversation starters</li>
                <li>• Travel suggestions</li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">🚀 Premium Features</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Unlimited AI recommendations</li>
                <li>• Advanced personality insights</li>
                <li>• Real-time safety alerts</li>
                <li>• Mobile app AI features</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading AI features...</p>
        </div>
      </div>
    );
  }

  return renderAIFeatures();
} 